/**
 * The game flow statechart (XState v5).
 *
 * Models a single play-through of the deal: briefing → playing (advancing through the linear
 * `playSequence`, with the prioritisation pop-up firing as a BLOCKING interrupt tied to its host
 * scenario) → finished. The whole run lives in `context`, so `actor.getPersistedSnapshot()`
 * serialises an exact, resumable save (see store/session.ts). Scoring is delegated to ./scoring.ts.
 *
 * Per-scenario flow (the interrupt fires before OR after the host, per its `timing`):
 *   scenarioStart ──(before-answer interrupt?)──▶ triaging ──SUBMIT_RANKING──▶ deciding
 *                 └──────────────────────────────────────────────────────────▶ deciding
 *   deciding ──SELECT (highlight)──▶ deciding ; ──COMMIT (locks)──▶ revealing
 *   revealing ──CONTINUE──(after-answer interrupt?)──▶ triagingAfter ──SUBMIT_RANKING──▶ advancing
 *             └────────────────────────────────────────────────────────────────────▶ advancing
 *   advancing ──(more scenarios?)──▶ scenarioStart ; ──(none left)──▶ finished
 *   A TICK that crosses the 45-minute limit (timed mode only) ends the run as a missed cut-off.
 */
import { assign, setup } from "xstate";
import { gameContent, interruptsByHost, playSequence } from "../content";
import {
  applyDelta,
  computeEnding,
  initialScores,
  popupDelta,
  type PopupRankEntry,
  type Scores,
} from "./scoring";

const TIME_LIMIT_MS = gameContent.meta.timeLimitSeconds * 1000;
/** Ending shown when the cut-off passes before the deal completes. */
const TIMEOUT_ENDING_ID = "collapsed";

export interface TranscriptEntry {
  scenarioId: string;
  kind: "choice" | "knowledge_check" | "popup";
  choiceId?: string;
  tier?: string;
  ranking?: PopupRankEntry[];
}

export interface GameContext {
  mode: "timed" | "untimed";
  /** Per-run seed for deterministic option shuffling (stable across resume; differs per run). */
  seed: number;
  index: number;
  scores: Scores;
  elapsedMs: number;
  selectedChoiceId: string | null;
  interruptDone: Record<string, boolean>;
  transcript: TranscriptEntry[];
  endingId: string | null;
}

export type GameEvent =
  | { type: "START"; mode: "timed" | "untimed"; seed: number }
  | { type: "SELECT"; choiceId: string }
  | { type: "COMMIT" }
  | { type: "CONTINUE" }
  | { type: "SUBMIT_RANKING"; ranking: PopupRankEntry[] }
  | { type: "TICK"; ms: number }
  | { type: "RESTART" };

/** Current scenario for the play index (undefined once the sequence is exhausted). */
export function scenarioAt(index: number) {
  return playSequence[index];
}

function freshContext(mode: GameContext["mode"], seed: number): GameContext {
  return {
    mode,
    seed,
    index: 0,
    scores: initialScores(gameContent),
    elapsedMs: 0,
    selectedChoiceId: null,
    interruptDone: {},
    transcript: [],
    endingId: null,
  };
}

export const gameMachine = setup({
  types: {
    context: {} as GameContext,
    events: {} as GameEvent,
  },
  guards: {
    // A mid-task interrupt fires BEFORE the host's options (timing on-enter / before-answer).
    needsInterruptBefore: ({ context }) => {
      const sc = scenarioAt(context.index);
      const pop = sc ? interruptsByHost.get(sc.id) : undefined;
      if (!pop || context.interruptDone[pop.id]) return false;
      const timing = pop.interrupt?.timing ?? "after-answer";
      return timing === "on-enter" || timing === "before-answer";
    },
    // An interrupt fires AFTER the player has answered the host (timing after-answer) — used by CLOSE-09
    // so its "continue resolving the qualification" task references a situation already on screen.
    needsInterruptAfter: ({ context }) => {
      const sc = scenarioAt(context.index);
      const pop = sc ? interruptsByHost.get(sc.id) : undefined;
      if (!pop || context.interruptDone[pop.id]) return false;
      return (pop.interrupt?.timing ?? "after-answer") === "after-answer";
    },
    hasSelection: ({ context }) => context.selectedChoiceId != null,
    isFinished: ({ context }) => context.index >= playSequence.length,
    timeUp: ({ context }) => context.mode === "timed" && context.elapsedMs >= TIME_LIMIT_MS,
  },
  actions: {
    startGame: assign(({ event }) =>
      freshContext(event.type === "START" ? event.mode : "timed", event.type === "START" ? event.seed : 0),
    ),
    setSelected: assign(({ event }) => ({
      selectedChoiceId: event.type === "SELECT" ? event.choiceId : null,
    })),
    applyChoice: assign(({ context }) => {
      const sc = scenarioAt(context.index);
      if (!sc || sc.kind === "popup") return {};
      const choice = sc.choices.find((c) => c.id === context.selectedChoiceId);
      if (!choice) return {};
      // Keep selectedChoiceId through the reveal so the UI knows which option to expand; cleared on advance.
      return {
        scores: applyDelta(context.scores, choice.score, gameContent),
        transcript: [
          ...context.transcript,
          { scenarioId: sc.id, kind: sc.kind, choiceId: choice.id, tier: choice.tier },
        ],
      };
    }),
    scorePopup: assign(({ context, event }) => {
      const sc = scenarioAt(context.index);
      const pop = sc ? interruptsByHost.get(sc.id) : undefined;
      if (!pop || event.type !== "SUBMIT_RANKING") return {};
      return {
        scores: applyDelta(context.scores, popupDelta(pop, event.ranking), gameContent),
        interruptDone: { ...context.interruptDone, [pop.id]: true },
        transcript: [...context.transcript, { scenarioId: pop.id, kind: "popup" as const, ranking: event.ranking }],
      };
    }),
    advance: assign(({ context }) => ({ index: context.index + 1, selectedChoiceId: null })),
    tick: assign(({ context, event }) => ({
      elapsedMs: context.elapsedMs + (event.type === "TICK" ? event.ms : 0),
    })),
    resolveEnding: assign(({ context }) => ({
      endingId: computeEnding(context.scores, gameContent.endings)?.id ?? TIMEOUT_ENDING_ID,
    })),
    resolveTimeoutEnding: assign({ endingId: TIMEOUT_ENDING_ID }),
  },
}).createMachine({
  id: "game",
  initial: "briefing",
  context: freshContext("timed", 0),
  states: {
    briefing: {
      on: { START: { target: "playing", actions: "startGame" } },
    },
    playing: {
      initial: "scenarioStart",
      // A clock tick that crosses the cut-off (timed mode) ends the run as a missed cut-off.
      on: {
        TICK: [
          { guard: "timeUp", target: ".timedOut", actions: "tick" },
          { actions: "tick" },
        ],
        // "Exit without saving" during play resets to the briefing (briefing is a sibling of playing).
        RESTART: { target: "briefing" },
      },
      states: {
        scenarioStart: {
          always: [
            { guard: "needsInterruptBefore", target: "triaging" },
            { target: "deciding" },
          ],
        },
        // Triage that precedes the host's options (timing before-answer).
        triaging: {
          on: { SUBMIT_RANKING: { target: "deciding", actions: "scorePopup" } },
        },
        deciding: {
          on: {
            SELECT: { actions: "setSelected" },
            COMMIT: { guard: "hasSelection", target: "revealing", actions: "applyChoice" },
          },
        },
        revealing: {
          on: {
            CONTINUE: [
              { guard: "needsInterruptAfter", target: "triagingAfter" },
              { target: "advancing" },
            ],
          },
        },
        // Triage that follows the host's reveal (timing after-answer), then continues to the next scenario.
        triagingAfter: {
          on: { SUBMIT_RANKING: { target: "advancing", actions: "scorePopup" } },
        },
        advancing: {
          entry: "advance",
          always: [
            { guard: "isFinished", target: "done", actions: "resolveEnding" },
            { target: "scenarioStart" },
          ],
        },
        done: { type: "final" },
        timedOut: { type: "final", entry: "resolveTimeoutEnding" },
      },
      onDone: { target: "finished" },
    },
    finished: {
      on: { RESTART: { target: "briefing" } },
    },
  },
});

export type GameMachine = typeof gameMachine;
